// 1 プレイヤー分のレイアウト (landscape、 2 段: バトル行 + 手札行)。
//
// バトル行: [墓地/除外] [SET] [field 5枠 (中央)] [場ｶｰﾄﾞ] [CHARGE/設置]
// 手札行:   [次元] [手札] [DECK]
//
// mirrored=true (相手側) で左右を入れ替え、 かつ 手札行を上 / バトル行を下にして
// field が中央 (HUD) 側に接する向きにする。 Board.tsx が上=相手 / 下=自分で並べる。

import type { CardView } from "@/bindings/CardView";
import type { PlayerView } from "@/bindings/PlayerView";
import { ChargeRow, FieldRow, OpponentHand, PlayerHand } from "./tiles";
import { CardPile, CardSlot, MiniZone } from "./zones";

interface PlayerMatProps {
  player: PlayerView;
  mirrored: boolean;
  // field 操作 (Board から渡す)
  onFieldClick?: ((card: CardView) => void) | undefined;
  fieldClickable?: boolean | undefined;
  fieldSelectedId?: string | null | undefined;
  fieldDisabled?: boolean | undefined;
  // hand 操作 (自分 mat のみ)
  canSummon?: boolean | undefined;
  onSummon?: ((card: CardView) => void) | undefined;
  canSetInitialCharge?: boolean | undefined;
  onSetInitialCharge?: ((card: CardView) => void) | undefined;
  canCharge?: boolean | undefined;
  onCharge?: ((card: CardView) => void) | undefined;
  busy?: boolean | undefined;
}

// バトル行の左ゾーン群: 墓地/除外 の縦 2 連 + SET (= 上限1枚、 通常カードサイズ、 field に隣接)。
function BattleLeft(props: { player: PlayerView }): JSX.Element {
  const { player } = props;
  const topGrave = player.grave[player.grave.length - 1];
  const sv = player.set_zone;
  const setCard = sv.cards?.[0] ?? null;
  return (
    <div className="flex items-center gap-1">
      <div className="flex flex-col gap-0.5">
        <MiniZone
          label="墓地"
          count={player.grave.length}
          topCardName={topGrave?.name ?? null}
        />
        <MiniZone label="除外" count={player.banished.length} />
      </div>
      <CardSlot label="SET" card={setCard} faceDown={setCard === null && sv.count > 0} />
    </div>
  );
}

// バトル行の右ゾーン群: 場ｶｰﾄﾞ (field に隣接) + CHARGE。
// 設置はメインフィールド扱いのため field 直下の横帯に置く (BattleBand 参照)。
// self (= cards あり) は ChargeRow で cards を可視化。相手は MiniZone (count のみ)。
function BattleRight(props: { player: PlayerView }): JSX.Element {
  const { player } = props;
  return (
    <div className="flex items-center gap-1">
      <CardSlot label="場ｶｰﾄﾞ" placeholder />
      <ChargeRow charge={player.charge} />
    </div>
  );
}

// 設置ゾーン (= installation、 メインフィールド扱い)。field 5枠の直下に同幅・カード高さの段。
function InstallBar(): JSX.Element {
  return (
    <div className="flex h-16 flex-col items-center justify-center gap-0.5 rounded border border-dashed border-slate-700 text-center">
      <span className="text-[9px] text-slate-500">設置</span>
      <span className="text-[8px] text-slate-700">未実装</span>
    </div>
  );
}

// バトル行: [左ゾーン] [field 5枠] [右ゾーン]。
function BattleBand(props: PlayerMatProps): JSX.Element {
  const {
    player,
    mirrored,
    onFieldClick,
    fieldClickable,
    fieldSelectedId,
    fieldDisabled,
  } = props;
  const left = mirrored ? (
    <BattleRight player={player} />
  ) : (
    <BattleLeft player={player} />
  );
  const right = mirrored ? (
    <BattleLeft player={player} />
  ) : (
    <BattleRight player={player} />
  );
  const fieldRow = (
    <FieldRow
      field={player.field}
      onCardClick={onFieldClick}
      selectedId={fieldSelectedId}
      clickable={fieldClickable}
      disabled={fieldDisabled}
    />
  );
  // 設置帯はモンスター行の直下 (自分) / 直上 (相手ミラー)。 side zones はモンスター行に揃える。
  return (
    <div
      className={`flex ${mirrored ? "items-end" : "items-start"} justify-center gap-3 rounded border border-slate-700 bg-slate-800/50 px-2 py-1`}
    >
      <div className="shrink-0">{left}</div>
      <div className="flex flex-col items-stretch gap-0.5">
        {mirrored ? (
          <>
            <InstallBar />
            {fieldRow}
          </>
        ) : (
          <>
            {fieldRow}
            <InstallBar />
          </>
        )}
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}

// 手札行: [次元] [手札] [DECK] (= deck/次元 を手札と同列に置く)。
function HandRow(props: PlayerMatProps): JSX.Element {
  const {
    player,
    mirrored,
    canSummon,
    onSummon,
    canSetInitialCharge,
    onSetInitialCharge,
    canCharge,
    onCharge,
    busy,
  } = props;
  const dimension = <CardSlot label="次元" placeholder />;
  const deck = <CardPile label="DECK" count={player.deck_count} />;
  const left = mirrored ? deck : dimension;
  const right = mirrored ? dimension : deck;
  const hand = mirrored ? (
    <OpponentHand hand={player.hand} />
  ) : (
    <PlayerHand
      hand={player.hand}
      canSummon={canSummon === true}
      canSetInitialCharge={canSetInitialCharge === true}
      canCharge={canCharge === true}
      busy={busy === true}
      onSummon={onSummon ?? (() => undefined)}
      onSetInitialCharge={onSetInitialCharge ?? (() => undefined)}
      onCharge={onCharge ?? (() => undefined)}
    />
  );
  return (
    <div className="flex items-center justify-center gap-3">
      <div className="shrink-0">{left}</div>
      <div className="flex items-center justify-center">{hand}</div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}

export function PlayerMat(props: PlayerMatProps): JSX.Element {
  const { mirrored } = props;
  // 相手 (mirrored): 手札行 → バトル行 (field が下端 = 中央側)。
  // 自分: バトル行 → 手札行 (field が上端 = 中央側)。
  return (
    <div className="flex flex-col gap-1">
      {mirrored ? (
        <>
          <HandRow {...props} />
          <BattleBand {...props} />
        </>
      ) : (
        <>
          <BattleBand {...props} />
          <HandRow {...props} />
        </>
      )}
    </div>
  );
}
